import {
  Apple,
  Barcode,
  Camera,
  ChevronLeft,
  ChevronRight,
  Layers,
  Mic,
  NotebookPen,
  Plus,
  ScanLine,
  Search,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { formatDay } from "../../lib/dayHelpers";
import type { MealType } from "./review-utils";
import { sourceChipLabel } from "./review-utils";

// Meal Review header (plan todo 12): editable meal name, photo thumbnail or
// source icon, meal-type segmented control, date day-stepper, editable
// logged-at time control, and the logging-source chip. Plain controls styled
// with the index.css tokens (PlateBar precedent) so the vitest module graph
// stays free of @/-aliased imports.

const MEAL_TYPE_OPTIONS: readonly { readonly value: MealType; readonly label: string }[] = [
  { value: "breakfast", label: "Breakfast" },
  { value: "lunch", label: "Lunch" },
  { value: "dinner", label: "Dinner" },
  { value: "snack", label: "Snack" },
];

const SOURCE_ICONS: Record<string, LucideIcon> = {
  photo: Camera,
  upload: Camera,
  text: NotebookPen,
  voice: Mic,
  barcode: Barcode,
  label: ScanLine,
  search: Search,
  quickAdd: Plus,
  plate: Layers,
};

export interface ReviewHeaderProps {
  readonly name: string;
  readonly mealType: MealType;
  readonly date: number;
  readonly time: string;
  readonly source?: string;
  // undefined = still loading, null = no photo, string = resolved URL.
  readonly imageUrl?: string | null;
  readonly onNameChange: (name: string) => void;
  readonly onMealTypeChange: (mealType: MealType) => void;
  readonly onStepDate: (days: number) => void;
  readonly onTimeChange: (time: string) => void;
}

export function ReviewHeader({
  name,
  mealType,
  date,
  time,
  source,
  imageUrl,
  onNameChange,
  onMealTypeChange,
  onStepDate,
  onTimeChange,
}: ReviewHeaderProps) {
  const SourceIcon = (source !== undefined ? SOURCE_ICONS[source] : undefined) ?? Apple;
  return (
    <section
      data-testid="review-header"
      className="rounded-2xl border border-border bg-surface p-4 shadow-sm"
    >
      <div className="flex items-center gap-3">
        {typeof imageUrl === "string" ? (
          <img
            src={imageUrl}
            alt="Meal photo"
            data-testid="review-photo"
            className="h-12 w-12 shrink-0 rounded-xl border border-border object-cover"
          />
        ) : (
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-accent-light text-accent">
            <SourceIcon size={22} strokeWidth={2} aria-hidden="true" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <label htmlFor="review-name-input" className="text-xs font-medium text-text-muted">
            Meal name
          </label>
          <input
            id="review-name-input"
            data-testid="review-name-input"
            type="text"
            value={name}
            onChange={(event) => onNameChange(event.target.value)}
            placeholder="e.g. Chicken biryani"
            className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-base font-semibold text-text placeholder:font-normal placeholder:text-text-muted focus:border-transparent focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>
      </div>

      <div
        role="group"
        aria-label="Meal type"
        className="mt-3 grid grid-cols-4 gap-1 rounded-xl bg-muted p-1"
      >
        {MEAL_TYPE_OPTIONS.map((option) => {
          const active = mealType === option.value;
          return (
            <button
              key={option.value}
              type="button"
              data-testid={`review-meal-type-${option.value}`}
              aria-pressed={active}
              onClick={() => onMealTypeChange(option.value)}
              className={`flex min-h-[44px] items-center justify-center rounded-lg px-1 text-sm font-semibold transition-all duration-200 ease-out active:scale-[0.98] select-none ${
                active ? "bg-accent text-white" : "text-text-muted active:bg-surface"
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <div className="flex flex-1 items-center justify-between rounded-xl border border-border bg-surface px-2 py-1">
          <button
            type="button"
            data-testid="review-date-prev"
            aria-label="Previous day"
            onClick={() => onStepDate(-1)}
            className="flex h-11 w-11 items-center justify-center rounded-lg text-text-muted transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] select-none"
          >
            <ChevronLeft size={18} strokeWidth={2} aria-hidden="true" />
          </button>
          <span data-testid="review-date-label" className="text-sm font-semibold text-text">
            {formatDay(date)}
          </span>
          <button
            type="button"
            data-testid="review-date-next"
            aria-label="Next day"
            onClick={() => onStepDate(1)}
            className="flex h-11 w-11 items-center justify-center rounded-lg text-text-muted transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] select-none"
          >
            <ChevronRight size={18} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
        <div className="flex items-center gap-1 rounded-xl border border-border bg-surface px-3 py-2">
          <label htmlFor="review-time-input" className="text-xs font-medium text-text-muted">
            Logged at
          </label>
          <input
            id="review-time-input"
            data-testid="review-time-input"
            type="time"
            value={time}
            onChange={(event) => onTimeChange(event.target.value)}
            className="bg-transparent text-base font-semibold text-text focus:outline-none"
          />
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <span
          data-testid="review-source-chip"
          className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs font-semibold text-text-muted"
        >
          <SourceIcon size={12} strokeWidth={2} aria-hidden="true" />
          {sourceChipLabel(source)}
        </span>
      </div>
    </section>
  );
}
